import {ArrowLeftIcon, ChevronRightIcon, SortAscIcon, SortDescIcon} from '@primer/octicons-react'
import {announce} from '@github-ui/aria-live'
import {ActionList, AnchoredOverlay, FocusKeys, IconButton, useFocusZone, VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import type React from 'react'
import {Fragment, useEffect, useId, useRef, useState} from 'react'

import {DirtyVisual} from './DirtyVisual'
import {findField, type FieldOption} from './field-option'
import {useOptionIntent} from './use-option-intent'
import styles from './ViewOptionsMenu.module.css'

export interface ViewOptionsSort {
  field: string
  direction: 'asc' | 'desc'
}

const SORT_DIRECTIONS = [
  {value: 'asc', label: 'Ascending', icon: SortAscIcon},
  {value: 'desc', label: 'Descending', icon: SortDescIcon},
] as const

/** The row's value, e.g. "↑ Milestone, ↓ Updated", with each direction spelled out for screen readers. */
function SortSummary({
  sorts,
  fields,
  placeholder,
}: {
  sorts: readonly ViewOptionsSort[]
  fields: readonly FieldOption[]
  placeholder: string
}) {
  if (sorts.length === 0) return placeholder
  return (
    <span className={styles.sortSummary}>
      {sorts.map((sort, index) => {
        const DirectionIcon = sort.direction === 'asc' ? SortAscIcon : SortDescIcon
        return (
          <Fragment key={sort.field}>
            {index > 0 && <span>, </span>}
            <DirectionIcon /> <span>{findField(fields, sort.field)?.label ?? sort.field}</span>
            <VisuallyHidden>{sort.direction === 'asc' ? ' (ascending)' : ' (descending)'}</VisuallyHidden>
          </Fragment>
        )
      })}
    </span>
  )
}

interface SortLevelProps {
  heading: string
  placeholder: string
  fields: readonly FieldOption[]
  value: ViewOptionsSort | null
  onChange: (next: ViewOptionsSort | null) => void
  onIntent?: (next: ViewOptionsSort | null) => void
  /** Shows a back button before the heading, named `backLabel`, that returns to the previous level. */
  onBack?: () => void
  backLabel?: string
  backRef?: React.Ref<HTMLButtonElement>
  /** Content after the field list, e.g. the "Then sort by" row. */
  children?: React.ReactNode
}

/**
 * One sort level's dialog content: a header with the level's heading and a direction toggle,
 * then a single-select listbox of fields led by `placeholder` to clear it. The toggle sits
 * outside the listbox so it is a plain button reached with `Tab`, while the listbox keeps
 * arrow-key navigation. Direction stays toggleable without a field: it is held and applied
 * with the next field.
 */
function SortLevel({
  heading,
  placeholder,
  fields,
  value,
  onChange,
  onIntent,
  onBack,
  backLabel,
  backRef,
  children,
}: SortLevelProps) {
  const headingId = useId()
  const listRef = useRef<HTMLUListElement>(null)
  const [pendingDirection, setPendingDirection] = useState<ViewOptionsSort['direction']>('asc')
  const direction = value?.direction ?? pendingDirection
  const directionOption = SORT_DIRECTIONS.find(option => option.value === direction) ?? SORT_DIRECTIONS[0]
  const otherDirectionOption = SORT_DIRECTIONS.find(option => option.value !== direction) ?? SORT_DIRECTIONS[0]
  const intent = useOptionIntent<string | null>(
    onIntent && (identifier => onIntent(identifier === null ? null : {field: identifier, direction})),
  )
  useFocusZone({
    containerRef: listRef,
    bindKeys: FocusKeys.ArrowVertical | FocusKeys.HomeAndEnd,
    focusOutBehavior: 'wrap',
    focusInStrategy: () =>
      listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]') ??
      listRef.current?.querySelector<HTMLElement>('[role="option"]:not([aria-disabled="true"])') ??
      undefined,
  })

  const pick = (event: React.SyntheticEvent, next: ViewOptionsSort | null) => {
    event.preventDefault()
    intent.cancel()
    setPendingDirection(next?.direction ?? direction)
    onChange(next)
  }

  const toggleDirection = () => {
    setPendingDirection(otherDirectionOption.value)
    if (value) onChange({...value, direction: otherDirectionOption.value})
    // A focused button's changed label is not reliably re-read, so state the result.
    announce(`${heading}: ${otherDirectionOption.label.toLowerCase()}`)
  }

  return (
    <div className={styles.sortDialog}>
      <div className={clsx(styles.sortDialogHeader, onBack && styles.sortDialogHeaderWithBack)}>
        <div className={styles.sortDialogTitle}>
          {onBack && (
            <IconButton
              ref={backRef}
              size="small"
              variant="invisible"
              icon={ArrowLeftIcon}
              aria-label={backLabel ?? 'Back'}
              onClick={onBack}
            />
          )}
          <h2 id={headingId} className={styles.sectionLabel}>
            {heading}
          </h2>
        </div>
        <IconButton
          size="small"
          variant="invisible"
          icon={directionOption.icon}
          aria-label={`Change sort direction to ${otherDirectionOption.label.toLowerCase()}`}
          description={`Sort: ${directionOption.label}`}
          onClick={toggleDirection}
        />
      </div>
      <ActionList
        ref={listRef}
        role="listbox"
        selectionVariant="single"
        aria-labelledby={headingId}
        // The zone above replaces ActionList's built-in one, which doesn't wrap and would handle arrows first.
        disableFocusZone
      >
        <ActionList.Item
          {...intent.propsFor(null, value !== null)}
          selected={value === null}
          onSelect={event => pick(event, null)}
        >
          {placeholder}
        </ActionList.Item>
        {fields.map(field => {
          const FieldIcon = field.icon
          const isSelected = value?.field === field.identifier
          return (
            <ActionList.Item
              key={field.identifier}
              {...intent.propsFor(field.identifier, !isSelected && !field.disabled)}
              selected={isSelected}
              disabled={field.disabled}
              onSelect={event => pick(event, {field: field.identifier, direction})}
            >
              <ActionList.LeadingVisual>
                <FieldIcon />
              </ActionList.LeadingVisual>
              {field.label}
            </ActionList.Item>
          )
        })}
      </ActionList>
      {children}
    </div>
  )
}

type SortDialogAnchorProps = Pick<
  React.HTMLAttributes<HTMLElement>,
  'id' | 'aria-haspopup' | 'aria-expanded' | 'onClick' | 'onKeyDown' | 'tabIndex'
> & {ref?: React.Ref<HTMLLIElement>}

interface SortDialogProps {
  /** Renders the row that opens the dialog, spreading the given anchor props onto it. */
  renderAnchor: (anchorProps: SortDialogAnchorProps) => React.JSX.Element
  /** The dialog's accessible name. */
  label: string
  /** Called on Escape. Returns `true` when it handled Escape, which keeps the dialog open. */
  onEscape?: () => boolean
  /** Called after the dialog closes. */
  onClosed?: () => void
  children: React.ReactNode
}

/**
 * A non-modal dialog flyout to the left of its row. It is a dialog rather than a menu so its
 * header can hold the direction button, which a `role="menu"` cannot contain. Escape closes
 * only the topmost flyout and returns focus to its row.
 */
function SortDialog({renderAnchor, label, onEscape, onClosed, children}: SortDialogProps) {
  const [open, setOpen] = useState(false)
  return (
    <AnchoredOverlay
      open={open}
      onOpen={() => setOpen(true)}
      onClose={gesture => {
        if (gesture === 'escape' && onEscape?.()) return
        setOpen(false)
        onClosed?.()
      }}
      renderAnchor={anchorProps => renderAnchor({...anchorProps, 'aria-haspopup': 'dialog'})}
      side="outside-left"
      align="start"
      width="medium"
      displayInViewport
      displayCloseButton={false}
      focusZoneSettings={{disabled: true}}
      cssAnchorPositioningSettings={{disable: true}}
      overlayProps={{role: 'dialog', 'aria-label': label}}
    >
      {children}
    </AnchoredOverlay>
  )
}

export interface ViewOptionsSortSelectProps {
  /** The row's leading icon. */
  icon: React.ComponentType<{size?: number}>
  /** The row's visible label, e.g. "Sort by". Also names the menu and its field group. */
  label: string
  /** Shown as the current value, and as the option that clears a level, when nothing is sorted. */
  placeholder: string
  /** The sortable fields, in display order. */
  fields: readonly FieldOption[]
  /** The applied sorts, primary first. Only the first two are shown. */
  sorts: readonly ViewOptionsSort[]
  /** Handler that is called with the sorts a pick produces, primary first. */
  onChange: (next: ViewOptionsSort[]) => void
  /**
   * Handler that is called with the sorts a field or clear option would produce, once the
   * pointer or focus stays on it long enough to suggest it is about to be picked.
   */
  onIntent?: (next: ViewOptionsSort[]) => void
  disabled?: boolean
  showDirtyIndicator?: boolean
}

/**
 * A sort row that opens a dialog with a direction toggle and a field listbox for the primary
 * sort. Once a primary field is chosen, a "Then sort by" row swaps the dialog's content for the
 * secondary sort, with a back button (or Escape) returning to the primary level. The secondary
 * level never offers the primary field. Clearing the primary sort clears both. Draws nothing
 * when handed no fields. Must be rendered inside a `<ViewOptionsMenu.Group>`.
 */
export function SortSelect({
  icon: Icon,
  label,
  placeholder,
  fields,
  sorts,
  onChange,
  onIntent,
  disabled,
  showDirtyIndicator = false,
}: ViewOptionsSortSelectProps) {
  const dirtyDescriptionId = useId()
  const [level, setLevel] = useState<'primary' | 'secondary'>('primary')
  const backRef = useRef<HTMLButtonElement>(null)
  const thenRowRef = useRef<HTMLLIElement>(null)
  // Where focus goes after the content swaps, since the focused control unmounts.
  const focusAfterSwapRef = useRef<'back' | 'thenRow' | null>(null)
  useEffect(() => {
    const target = focusAfterSwapRef.current
    focusAfterSwapRef.current = null
    if (target === 'back') backRef.current?.focus()
    else if (target === 'thenRow') thenRowRef.current?.focus()
  }, [level])

  if (fields.length === 0) return null

  const primary = sorts[0] ?? null
  const secondary = sorts[1] ?? null
  const secondaryFields = fields.filter(field => field.identifier !== primary?.field)

  const withPrimary = (next: ViewOptionsSort | null): ViewOptionsSort[] => {
    if (next === null) return []
    return secondary && secondary.field !== next.field ? [next, secondary] : [next]
  }
  const withSecondary = (next: ViewOptionsSort | null): ViewOptionsSort[] =>
    primary === null ? [] : next === null ? [primary] : [primary, next]

  const thenLabel = 'Then sort by'
  const thenSummary = (
    <span className={styles.trailingValue}>
      <SortSummary sorts={secondary ? [secondary] : []} fields={fields} placeholder={placeholder} />
      <ChevronRightIcon />
    </span>
  )

  const describedBy = showDirtyIndicator ? dirtyDescriptionId : undefined
  const renderTrigger = (anchorProps?: SortDialogAnchorProps) => (
    <ActionList.Item {...anchorProps} disabled={disabled} aria-describedby={describedBy}>
      <ActionList.LeadingVisual>
        <DirtyVisual showDirtyIndicator={showDirtyIndicator}>
          <Icon />
        </DirtyVisual>
      </ActionList.LeadingVisual>
      {label}
      {showDirtyIndicator && (
        <span id={dirtyDescriptionId} hidden>
          Unsaved changes
        </span>
      )}
      <ActionList.TrailingVisual>
        <span className={styles.trailingValue}>
          <SortSummary sorts={sorts.slice(0, 2)} fields={fields} placeholder={placeholder} />
          <ChevronRightIcon />
        </span>
      </ActionList.TrailingVisual>
    </ActionList.Item>
  )
  if (disabled) return renderTrigger()

  const showSecondary = () => {
    focusAfterSwapRef.current = 'back'
    setLevel('secondary')
  }
  const showPrimary = () => {
    focusAfterSwapRef.current = 'thenRow'
    setLevel('primary')
  }

  const thenRow =
    fields.length > 1 && primary !== null ? (
      <ActionList className={styles.sortDialogFooter}>
        <ActionList.Item ref={thenRowRef} onSelect={showSecondary}>
          {thenLabel}
          <ActionList.TrailingVisual>{thenSummary}</ActionList.TrailingVisual>
        </ActionList.Item>
      </ActionList>
    ) : null

  return (
    <SortDialog
      label={level === 'secondary' ? thenLabel : label}
      renderAnchor={renderTrigger}
      onEscape={() => {
        if (level !== 'secondary') return false
        showPrimary()
        return true
      }}
      onClosed={() => setLevel('primary')}
    >
      {level === 'secondary' && primary !== null ? (
        <SortLevel
          key="secondary"
          heading={thenLabel}
          placeholder={placeholder}
          fields={secondaryFields}
          value={secondary}
          onChange={next => onChange(withSecondary(next))}
          onIntent={onIntent && (next => onIntent(withSecondary(next)))}
          onBack={showPrimary}
          backLabel={`Back to ${label}`}
          backRef={backRef}
        />
      ) : (
        <SortLevel
          key="primary"
          heading={label}
          placeholder={placeholder}
          fields={fields}
          value={primary}
          onChange={next => onChange(withPrimary(next))}
          onIntent={onIntent && (next => onIntent(withPrimary(next)))}
        >
          {thenRow}
        </SortLevel>
      )}
    </SortDialog>
  )
}
